import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { Fragment } from 'react';
import { useDispatch } from 'react-redux';

import QuestionField from '../components/questionField';
import { QuestionFormWarnings } from '../components/questionWarnings';

import { useProgramQuestions, useSaveProgramQuestion } from 'hooks/useProgramQuestions';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type { QuestionnaireForm } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import {
    answersForSave,
    initialAnswers,
    isQuestionActive,
    optionsForQuestion,
    serverFieldErrors,
    validationSchema,
    visibleQuestions,
} from 'views/v2/programQuestions/formState';

interface ProgramQuestionFormProps {
    country: string;
    programId?: string | null;
    formName?: QuestionnaireForm;
    userId?: number;
    onSaved?: () => void;
    onCancel?: () => void;
}

const ProgramQuestionForm = ({
    country,
    programId = null,
    formName = 'program-question',
    userId,
    onSaved,
    onCancel,
}: ProgramQuestionFormProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const questionsQuery = useProgramQuestions(country, userId, formName, programId);
    const save = useSaveProgramQuestion();

    if (questionsQuery.isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (questionsQuery.isError || !questionsQuery.data) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">Could not load these questions.</Typography>
                {onCancel ? (
                    <Button onClick={onCancel} sx={{ mt: 2, textTransform: 'none' }}>
                        Close
                    </Button>
                ) : null}
            </Box>
        );
    }

    const form = questionsQuery.data;
    const questions = form.questions;

    return (
        <Formik
            enableReinitialize
            validateOnMount
            initialValues={initialAnswers(questions)}
            validationSchema={validationSchema(questions)}
            onSubmit={async (values, helpers) => {
                try {
                    await save.mutateAsync({
                        country,
                        program_id: programId,
                        form: formName,
                        user_id: userId,
                        value: answersForSave(questions, values),
                    });
                    dispatch(
                        setAlert({
                            message: 'Answers saved',
                            type: 'success',
                        })
                    );
                    onSaved?.();
                } catch (error) {
                    const payload =
                        error && typeof error === 'object' && 'response' in error
                            ? (error as { response?: { data?: unknown } }).response?.data
                            : undefined;
                    const fields = serverFieldErrors(payload);
                    if (Object.keys(fields).length > 0) {
                        helpers.setErrors(fields);
                    }
                    helpers.setSubmitting(false);
                    dispatch(
                        setAlert({
                            message:
                                Object.keys(fields).length > 0
                                    ? Object.values(fields).join(' ')
                                    : getErrorMessage(error, 'Could not save these answers'),
                            type: 'error',
                        })
                    );
                }
            }}
        >
            {({ values, setFieldValue, isSubmitting }) => {
                const activeQuestions = visibleQuestions(questions, values);
                return (
                    <Form
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            flex: 1,
                        }}
                    >
                        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, flexShrink: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {form.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {translate('programQuestionVisibleCount').replace(
                                    '{{count}}',
                                    String(activeQuestions.length)
                                )}
                            </Typography>
                        </Box>

                        <Box
                            sx={{ overflowY: 'auto', px: 3, py: 1, flex: '1 1 auto', minHeight: 0 }}
                        >
                            <Stack spacing={3}>
                                {activeQuestions.map((question, index) => {
                                    const previous = activeQuestions[index - 1];
                                    const showSection = Boolean(
                                        question.section && question.section !== previous?.section
                                    );
                                    return (
                                        <Fragment key={question.name}>
                                            {showSection ? (
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 700,
                                                        pt: index === 0 ? 0 : 1,
                                                    }}
                                                >
                                                    {question.section}
                                                </Typography>
                                            ) : null}
                                            <QuestionField
                                                question={question}
                                                active={true}
                                                onValueChange={(value) => {
                                                    const next = {
                                                        ...values,
                                                        [question.name]: value,
                                                    };
                                                    const clears = questions.flatMap((other) => {
                                                        if (!other.depends_on) {
                                                            return [];
                                                        }
                                                        if (
                                                            !isQuestionActive(
                                                                other,
                                                                next,
                                                                questions
                                                            )
                                                        ) {
                                                            return [
                                                                setFieldValue(
                                                                    other.name,
                                                                    other.preserve_when_hidden
                                                                        ? (other.value ?? '')
                                                                        : other.type ===
                                                                            'checkboxes'
                                                                          ? []
                                                                          : ''
                                                                ),
                                                            ];
                                                        }
                                                        if (
                                                            other.type !== 'select' ||
                                                            !other.options_by
                                                        ) {
                                                            return [];
                                                        }
                                                        const current = values[other.name];
                                                        const allowed = optionsForQuestion(
                                                            other,
                                                            next
                                                        );
                                                        if (
                                                            typeof current === 'string' &&
                                                            current.trim() &&
                                                            !allowed.includes(current.trim())
                                                        ) {
                                                            return [setFieldValue(other.name, '')];
                                                        }
                                                        return [];
                                                    });
                                                    return Promise.all(clears);
                                                }}
                                            />
                                        </Fragment>
                                    );
                                })}
                            </Stack>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 1,
                                px: 3,
                                py: 2,
                                flexShrink: 0,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <QuestionFormWarnings questions={questions} />
                            {onCancel ? (
                                <Button
                                    onClick={onCancel}
                                    disabled={isSubmitting}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Cancel
                                </Button>
                            ) : null}
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting}
                                sx={{ textTransform: 'none' }}
                            >
                                Save
                            </Button>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ProgramQuestionForm;
